import { useState } from 'react';
import { useApp } from '../store';
import {
  policyProblem,
  readConfig,
  readPolicy,
  sheetFor,
  sheetGroups,
  stepLabel,
  unassignedNames,
  usesSlots,
  usesStages,
  writeConfig,
  writePolicy,
  type SheetRow,
  type TheWorld,
} from '../signoff-policy';
import { DOCUMENT_KIND, isReceived } from '../documents';
import { thePanel } from '../signatories';
import type {
  DocumentKind,
  SignoffPolicy,
  SignoffStage,
  SignoffStageMember,
  SignoffStageMode,
  StoredSignoffPolicy,
} from '../types';
import Icon from './Icon';
import InfoTip from './InfoTip';
import { StageCard, sameMember } from './StageBuilder';

/**
 * ── THE THINGS A BOARD CAN OVERRIDE, IN THE BOARD'S OWN LANGUAGE — R1 ──────────
 *
 * A sign-off is raised for a sprint, a release, or a document; the document kinds
 * that are SIGNED (never the received/reference kinds — `isReceived` is the test)
 * each get their own row, named with the label the rest of the app uses
 * (`DOCUMENT_KIND`). Order follows Vinnie's list: the two subjects first, then the
 * documents a board produces.
 *
 * ⚠️ THE CHARTER IS DELIBERATELY OMITTED (Vinnie, 30 Sep 2026). A charter still
 * signs through its own bespoke send-screen signatory list, NOT the board policy,
 * so a charter override would be an inert control ([[every-action-intentional]]).
 * It rejoins this list once #445 folds the charter path onto the stage engine.
 */
const OVERRIDE_DOC_ORDER: DocumentKind[] = [
  'requirements',
  'change',
  'feature',
  'improvement',
  'deliverables',
  'test',
];

export const SIGNOFF_TYPES: { key: string; label: string }[] = [
  { key: 'sprint', label: 'Sprint' },
  { key: 'release', label: 'Release' },
  ...OVERRIDE_DOC_ORDER.filter((k) => !isReceived(k)).map((k) => ({ key: k, label: DOCUMENT_KIND[k] })),
];

/**
 * ── WHO SIGNS WORK OFF HERE, AS ORDERED STAGES — configurable sign-offs R1 ─────
 *
 * This replaces the two earlier editors (the rank chain and the named slots)
 * with the one model R1 settled on: a board writes its OWN ordered stages and
 * says, per stage, who answers it and how many of them must. A stage's signer is
 * a rank read fresh each time, a named person on the board, or somebody outside
 * it reached by email — the three kinds `signoff-policy.ts` resolves.
 *
 * ⚠️ THE PREVIEW AND THE WARNINGS ARE READ THROUGH THE ENGINE, not computed
 * here. "Who this asks, in order" is `sheetFor` and the warning is
 * `policyProblem` — the very functions the store will use when it raises one — so
 * the screen can never promise a sheet different from the one that arrives.
 *
 * ⚠️ THE STORE IS NOT TOUCHED. Editing writes the stages through `writePolicy`
 * to `meta.signoffPolicy`, exactly the save path the older editors used; the
 * raise/freeze path is handled separately and reads the same field.
 */

export default function SignoffRule({ canSettings }: { canSettings: boolean }) {
  const meta = useApp((s) => s.meta);
  const setMeta = useApp((s) => s.setMeta);
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const projectRoles = useApp((s) => s.projectRoles);
  const profiles = useApp((s) => s.profiles);
  const projectId = useApp((s) => s.projectId);

  const world: TheWorld = {
    owner: {
      name: (meta.projectOwnerName ?? '').trim(),
      position: (meta.projectOwnerPosition ?? '').trim(),
      email: (meta.projectOwnerEmail ?? '').trim(),
    },
    access,
    roles,
    projectRoles,
    profiles,
    projectId,
  };

  /*
   * THE BOARD'S SIGN-OFF, AS DEFAULT + OVERRIDES — configurable sign-offs R1.
   *
   * `readConfig` normalises whatever the board stores — a flat policy (every
   * board that has never overridden a type) or a `{default, overrides}` config —
   * into the two parts this screen edits. The stage editor here writes the
   * DEFAULT; the panel below writes per-type overrides. Every write goes back
   * through `writeConfig`, which drops back to the flat shape when a board has no
   * overrides, so a board that never touches the panel keeps the shape it had.
   */
  const config = readConfig(meta.signoffPolicy);
  const defaultPolicy = readPolicy(config.default);
  const onStages = usesStages(defaultPolicy);
  const legacyPresent = !onStages && (defaultPolicy.steps.length > 0 || usesSlots(defaultPolicy));
  const stages = defaultPolicy.stages ?? [];

  const writeCfg = (next: { default: StoredSignoffPolicy; overrides: Record<string, StoredSignoffPolicy> }) =>
    setMeta({ signoffPolicy: writeConfig(next) });

  /* THE DEFAULT'S STAGES, saved WITHOUT disturbing the overrides. Every save
     renumbers `order` to the array position, so the number on a card is always
     the order it is asked in — reordering is just moving the row. */
  const saveDefault = (next: SignoffStage[]) => {
    const policy: SignoffPolicy = {
      steps: [],
      named: [],
      onSprintFinish: defaultPolicy.onSprintFinish,
      stages: next.map((s, i) => ({ ...s, order: i })),
    };
    writeCfg({ default: writePolicy(policy), overrides: config.overrides });
  };

  /* ONE OVERRIDE'S STAGES, written under its type key — same renumbering, and the
     default and the other overrides are carried across untouched. */
  const saveOverride = (key: string, next: SignoffStage[]) => {
    const policy: SignoffPolicy = {
      steps: [],
      named: [],
      onSprintFinish: defaultPolicy.onSprintFinish,
      stages: next.map((s, i) => ({ ...s, order: i })),
    };
    writeCfg({ default: config.default, overrides: { ...config.overrides, [key]: writePolicy(policy) } });
  };

  /* AN OVERRIDE STARTS AS A COPY OF THE DEFAULT — the board tweaks what differs
     rather than building from nothing. Fresh ids so the copy is its own. If the
     default is not on stages yet, seed a single blank stage to fill in. */
  const seedOverride = (): StoredSignoffPolicy => {
    const from = defaultPolicy.stages ?? [];
    const seeded: SignoffStage[] = from.length
      ? from.map((s, i) => ({ ...s, id: crypto.randomUUID(), order: i, members: s.members.map((m) => ({ ...m })) }))
      : [{ id: crypto.randomUUID(), label: '', order: 0, members: [], mode: 'all' }];
    return writePolicy({ steps: [], named: [], onSprintFinish: defaultPolicy.onSprintFinish, stages: seeded });
  };

  const [openKey, setOpenKey] = useState<string | null>(null);

  const addOverride = (key: string) => {
    writeCfg({ default: config.default, overrides: { ...config.overrides, [key]: seedOverride() } });
    setOpenKey(key);
  };
  const resetOverride = (key: string) => {
    const { [key]: _drop, ...rest } = config.overrides;
    void _drop;
    writeCfg({ default: config.default, overrides: rest });
    if (openKey === key) setOpenKey(null);
  };

  const sheet = onStages ? sheetFor(defaultPolicy, world) : [];
  const asked = sheet.length;

  /*
   * THE EARLIER SETUP, READ THROUGH THE SAME ENGINE — configurable sign-offs R1.
   *
   * A board still on the rank chain or the named slots must show WHO it currently
   * asks, grouped by step, so an existing board's rule is VISIBLE while it decides
   * whether to move to stages — not a blank editor that reads as "nobody signs
   * here" on a board that has signed work off for months (the regression the gate
   * caught). Read-only: the way to change it is to add stages above, as the note
   * says. Named people ride in from the panel exactly as the store injects them at
   * raise time (`{ ...policy, named: meta.signatories }`), and slots' developer row
   * is a raise-time attestation, so a preview shows no developer — see slotsSheet.
   */
  const legacyPolicy: SignoffPolicy = { ...defaultPolicy, named: meta.signatories ?? [] };
  /*
   * A PLAIN NAMED BOARD — no ranks, steps, slots or stages, but people named in
   * the panel above (and maybe an owner). Its documents and releases are signed
   * by that panel (`thePanel`, one approval), so it DOES have signers even though
   * the rule carries no model. Show them rather than "nobody signs here" — the
   * same fix, one rung simpler, as drawing a rank/slots board's rule (Vinnie,
   * 30 September 2026). `thePanel` is read only for this case, so a rank or slots
   * board still resolves through the engine below.
   */
  const panel = !onStages && !legacyPresent ? thePanel(meta) : [];
  const plainPresent = panel.length > 0;
  const earlierRule = legacyPresent || plainPresent;
  const legacySheet: SheetRow[] = legacyPresent
    ? sheetFor(legacyPolicy, world)
    : plainPresent
      ? panel.map((who) => ({ step: '' as const, who }))
      : [];
  const legacyGroups = sheetGroups(legacySheet, true);
  /* A typed name the older chain asks nothing (#352): named on the screen rather
     than silently dropped, the same promise the old editor kept. */
  const legacyUnasked = legacyPresent ? unassignedNames(legacyPolicy) : [];
  const nobodySet = !onStages && !earlierRule;

  return (
    <div className="sr-stages fd-form">
      <section className="fd-sec sr-block">
        <h3 className="fd-sec-h">
          The stages, in the order work is approved{' '}
          <InfoTip label="the stages">
            Work is approved in the order you list here: each stage is asked only once the stage before
            it has answered, so nobody signs off work that has not been reviewed yet. A stage can ask a
            rank (worked out fresh each time), a person on this board, or someone outside it by email.
          </InfoTip>
        </h3>
        <p className="sr-note">
          Each stage is asked only once the stage before it has answered, so nobody approves work that
          has not been reviewed yet. A stage can ask a rank (worked out fresh each time), a person on
          this board, or someone outside it by email.
        </p>

        {/* BOARD DEFAULT. Every kind of work uses these stages unless it is
            overridden below — the pinned row names that, so the stages read as the
            default rather than as a one-off. */}
        <div className="sr-default-bar">
          <span className="sr-default-tag">Board default</span>
          <span className="sr-default-why">
            Every kind of work uses these stages unless you override it below.
          </span>
        </div>

        {legacyPresent && (
          <p className="sr-note sr-legacy" role="status">
            This board still uses its earlier sign-off setup. Add the stages you want below to move it
            to the ordered model — the earlier setup keeps working until you do.
          </p>
        )}

        <StageEditor
          stages={stages}
          onChange={saveDefault}
          world={world}
          profiles={profiles}
          canSettings={canSettings}
          emptyNote={
            !onStages && !earlierRule
              ? 'Nobody is set to sign work off here. Add a stage and say who signs it.'
              : undefined
          }
        />
      </section>

      {/* WHO THIS ASKS, IN ORDER — read through the engine, grouped by stage, so
          "the project owner" is shown as the person it means today. */}
      {onStages && stages.length > 0 && (
        <section className="fd-sec sr-block">
          <h3 className="fd-sec-h">
            Who this asks, in order{' '}
            <InfoTip label="who this asks">
              A live read of who your setup asks right now, worked out from the stages and people above
              and grouped by step. It updates as you change them &mdash; so change who signs by editing
              the setup above, not here.
            </InfoTip>
          </h3>
          <div className="sr-who">
            <span className="sr-who-count">
              Right now that is {asked} {asked === 1 ? 'person' : 'people'} across {stages.length}{' '}
              {stages.length === 1 ? 'stage' : 'stages'}.
            </span>
            {stages.map((stage) => {
              const here = sheet.filter((r) => r.step === stage.id);
              return (
                <div className="sr-who-step" key={stage.id}>
                  <span className="grp-heading">
                    {stage.label.trim() || 'Untitled stage'}
                    {stage.mode === 'anyOne' && <span className="sr-who-mode"> — any one of them</span>}
                    {typeof stage.mode === 'object' && here.length >= 3 && (
                      <span className="sr-who-mode">
                        {' '}— any {Math.min(Math.max(stage.mode.quorum, 2), here.length - 1)} of them
                      </span>
                    )}
                    {stage.optional && <span className="sr-who-mode"> — optional</span>}
                  </span>
                  {here.length === 0 ? (
                    <p className="sr-note problem">This stage asks nobody right now.</p>
                  ) : (
                    <ol className="sr-who-list">
                      {here.map(({ who }) => (
                        <li key={`${who.name}|${who.position}|${who.email ?? ''}`}>
                          <strong>{who.name}</strong> — {who.position}
                          {who.email ? '' : ' (no account here)'}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* THE EARLIER SETUP'S RULE, DRAWN — a legacy rank/slots board, or a plain
          named board, shows who it currently asks (read through the same engine,
          grouped by step), so its rule is never hidden behind an empty stage
          editor. Read-only; adding stages above is the way to change it. */}
      {earlierRule && (
        <section className="fd-sec sr-block">
          <h3 className="fd-sec-h">
            Who this asks, in order{' '}
            <InfoTip label="who this asks">
              A live read of who your setup asks right now, worked out from the stages and people above
              and grouped by step. It updates as you change them &mdash; so change who signs by editing
              the setup above, not here.
            </InfoTip>
          </h3>
          <div className="sr-who">
            {legacySheet.length === 0 ? (
              <p className="sr-note problem">
                The earlier setup asks nobody right now — nothing would be signed off until you add
                stages above.
              </p>
            ) : (
              <>
                <span className="sr-who-count">
                  Right now that is {legacySheet.length} {legacySheet.length === 1 ? 'person' : 'people'}
                  {legacyGroups.length > 1 ? ` across ${legacyGroups.length} steps` : ''}.
                </span>
                {legacyGroups.length === 0 ? (
                  <div className="sr-who-step">
                    <ol className="sr-who-list">
                      {legacySheet.map(({ who }) => (
                        <li key={`${who.name}|${who.position}|${who.email ?? ''}`}>
                          <strong>{who.name}</strong> — {who.position}
                          {who.email ? '' : ' (no account here)'}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  legacyGroups.map((step) => {
                    const here = legacySheet.filter((r) => (r.step ?? '') === step);
                    return (
                      <div className="sr-who-step" key={step}>
                        <span className="grp-heading">{stepLabel(step, meta.signoffPolicy)}</span>
                        {here.length === 0 ? (
                          <p className="sr-note problem">This step asks nobody right now.</p>
                        ) : (
                          <ol className="sr-who-list">
                            {here.map(({ who }) => (
                              <li key={`${who.name}|${who.position}|${who.email ?? ''}`}>
                                <strong>{who.name}</strong> — {who.position}
                                {who.email ? '' : ' (no account here)'}
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            )}
            {legacyUnasked.map((who) => (
              <p className="sr-note problem" key={`${who.name}|${who.position}|${who.email ?? ''}`}>
                <strong>{who.name}</strong>
                {who.position ? ` (${who.position})` : ''} is on the panel but no step asks them.
              </p>
            ))}
          </div>
        </section>
      )}

      {/* ── BY WORK TYPE — the per-type overrides, Vinnie 30 Sep 2026 ──────────
          Every type inherits the board default; a type is overridden only when it
          is signed differently. Only the types that are overridden stop tracking
          the default. */}
      <section className="fd-sec sr-block sr-bywork">
        <h3 className="fd-sec-h">
          By work type{' '}
          <InfoTip label="by work type">
            Every work type inherits the board default above. Override a type only when it is signed
            differently &mdash; a release by the IT managers alone, say, with no owner. The types you
            do not override keep following the default on their own.
          </InfoTip>
        </h3>
        <p className="sr-note">
          Every work type inherits the board default above. Override a type when it is signed
          differently — a release by the IT managers only, say, with no owner. Only the ones you
          override stop tracking the default.
        </p>

        <div className="sr-type-list">
          <div className="sr-type-default">
            <span className="sr-type-name">Board default</span>
            {stages.length ? (
              <span className="sr-type-chain">{chainSummary(stages)}</span>
            ) : (
              <span className="sr-type-chain sr-type-chain-empty">No stages set yet.</span>
            )}
            <span className="sr-type-actions sr-type-editabove">Edit above</span>
          </div>

          {SIGNOFF_TYPES.map((t) => (
            <TypeRow
              key={t.key}
              typeKey={t.key}
              label={t.label}
              override={config.overrides[t.key]}
              open={openKey === t.key}
              world={world}
              profiles={profiles}
              canSettings={canSettings}
              onOverride={() => addOverride(t.key)}
              onToggleOpen={() => setOpenKey(openKey === t.key ? null : t.key)}
              onReset={() => resetOverride(t.key)}
              onChange={(next) => saveOverride(t.key, next)}
            />
          ))}
        </div>
      </section>

      {/* WHEN TO ASK — orthogonal to the model and carried across unchanged. It is
          a board-wide setting, read from the default. */}
      <section className="fd-sec sr-block">
        <h3 className="fd-sec-h">
          When to ask{' '}
          <InfoTip label="when to ask">
            When FlightDeck asks for sign-off on its own &mdash; the moment a sprint is finished, say &mdash;
            instead of waiting for someone to request it by hand. It is a board-wide setting and applies
            whichever approval model you use above.
          </InfoTip>
        </h3>
        <div className="sr-list">
          <div className="sr-switch-row">
            <button
              type="button"
              className={`switch ${defaultPolicy.onSprintFinish ? 'on' : ''}`}
              role="switch"
              aria-checked={defaultPolicy.onSprintFinish}
              aria-label="Ask automatically when a sprint is finished"
              disabled={!canSettings}
              onClick={() =>
                /* WHEN-TO-ASK IS ORTHOGONAL TO THE MODEL, so flipping it must
                   preserve whichever model the default is on and every override.
                   Spreading `defaultPolicy` keeps the default's model — stages,
                   steps or slots — and only the switch changes; the overrides ride
                   through `writeConfig` untouched. */
                writeCfg({
                  default: writePolicy({ ...defaultPolicy, onSprintFinish: !defaultPolicy.onSprintFinish }),
                  overrides: config.overrides,
                })
              }
            >
              <span className="switch-knob" />
            </button>
            <span className="sr-opt-name">Ask automatically when a sprint is finished</span>
            <span className="sr-opt-why">
              {nobodySet
                ? 'Nobody is set to sign, so nothing would be asked. Add a stage above first.'
                : 'Finishing a sprint raises the sign-off to these stages. It never stops the sprint finishing.'}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

/** A one-line summary of a set of stages — the labels, joined by arrows, the way
 *  a collapsed override reads on the By-work-type panel. */
function chainSummary(stages: SignoffStage[]): string {
  return stages.map((s) => s.label.trim() || 'Untitled stage').join(' → ');
}

/**
 * ── THE EDITABLE STAGE MACHINERY, REUSED — configurable sign-offs R1 ──────────
 *
 * The list of stage cards, the add-stage button and the "this rule asks nobody"
 * warning, factored out of the top-level component so the board default and each
 * per-type override edit through exactly the same controls. It owns its own drag
 * state and reads its warning through `policyProblem`, the very function the store
 * checks before it raises — so the editor can never promise a rule the raise
 * would refuse.
 */
function StageEditor({
  stages,
  onChange,
  world,
  profiles,
  canSettings,
  emptyNote,
}: {
  stages: SignoffStage[];
  onChange: (next: SignoffStage[]) => void;
  world: TheWorld;
  profiles: TheWorld['profiles'];
  canSettings: boolean;
  emptyNote?: string;
}) {
  const [dragAt, setDragAt] = useState<number | null>(null);

  const patch = (id: string, change: Partial<SignoffStage>) =>
    onChange(stages.map((s) => (s.id === id ? { ...s, ...change } : s)));

  const addStage = () =>
    onChange([
      ...stages,
      { id: crypto.randomUUID(), label: '', order: stages.length, members: [], mode: 'all' },
    ]);

  const removeStage = (id: string) => onChange(stages.filter((s) => s.id !== id));

  const moveStage = (at: number, dir: -1 | 1) => {
    const to = at + dir;
    if (to < 0 || to >= stages.length) return;
    const next = [...stages];
    [next[at], next[to]] = [next[to], next[at]];
    onChange(next);
  };

  const dropStage = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...stages];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const addMember = (id: string, member: SignoffStageMember) => {
    const stage = stages.find((s) => s.id === id);
    if (!stage || stage.members.some((m) => sameMember(m, member))) return;
    patch(id, { members: [...stage.members, member] });
  };

  const removeMember = (id: string, at: number) => {
    const stage = stages.find((s) => s.id === id);
    if (!stage) return;
    const members = stage.members.filter((_, i) => i !== at);
    /* Keep the answer rule honest when the pool shrinks: a quorum that now meets
       or exceeds what is left is no longer "at least N of several", so it falls
       back to everyone — the same clamp readStageMode makes on the way in, done
       here so the stored value never outlives its members. */
    const next: Partial<SignoffStage> = { members };
    if (typeof stage.mode === 'object' && (members.length < 3 || stage.mode.quorum > members.length - 1))
      next.mode = 'all';
    patch(id, next);
  };

  const setMode = (id: string, mode: SignoffStageMode) => patch(id, { mode });

  const policy: SignoffPolicy = { steps: [], named: [], onSprintFinish: false, stages };
  const problem = stages.length ? policyProblem(policy, world) : '';

  return (
    <>
      <div className="sr-stage-list">
        {stages.map((stage, at) => (
          <StageCard
            key={stage.id}
            stage={stage}
            at={at}
            total={stages.length}
            world={world}
            profiles={profiles}
            canSettings={canSettings}
            dragging={dragAt === at}
            onDragStart={() => setDragAt(at)}
            onDragEnd={() => setDragAt(null)}
            onDropHere={() => {
              if (dragAt !== null) dropStage(dragAt, at);
              setDragAt(null);
            }}
            onRename={(label) => patch(stage.id, { label })}
            onRemove={() => removeStage(stage.id)}
            onMove={(dir) => moveStage(at, dir)}
            onAddMember={(m) => addMember(stage.id, m)}
            onRemoveMember={(i) => removeMember(stage.id, i)}
            onMode={(mode) => setMode(stage.id, mode)}
          />
        ))}
      </div>

      {canSettings && (
        <button className="btn btn-small sr-add-stage" onClick={addStage}>
          <Icon name="plus" size={16} /> Add a stage
        </button>
      )}

      {problem && (
        <p className="sr-note problem" role="status">
          {problem}
        </p>
      )}
      {stages.length === 0 && emptyNote && (
        <p className="sr-note" role="status">
          {emptyNote}
        </p>
      )}
    </>
  );
}

/**
 * ── ONE WORK-TYPE ROW ON THE BY-WORK-TYPE PANEL — configurable sign-offs R1 ────
 *
 * A type either tracks the board default (a grey pill and an "Override" link) or
 * has its own stages (a highlighted pill, a one-line chain summary, and the
 * choice to edit it or reset it back to the default). Overriding opens the same
 * `StageEditor` the board default uses, titled with the type's name.
 */
function TypeRow({
  typeKey,
  label,
  override,
  open,
  world,
  profiles,
  canSettings,
  onOverride,
  onToggleOpen,
  onReset,
  onChange,
}: {
  typeKey: string;
  label: string;
  override: StoredSignoffPolicy | undefined;
  open: boolean;
  world: TheWorld;
  profiles: TheWorld['profiles'];
  canSettings: boolean;
  onOverride: () => void;
  onToggleOpen: () => void;
  onReset: () => void;
  onChange: (next: SignoffStage[]) => void;
}) {
  const stages = override ? readPolicy(override).stages ?? [] : [];
  const has = !!override;

  return (
    <div className={`sr-type ${has ? 'is-override' : ''} ${open ? 'is-open' : ''}`} data-type={typeKey}>
      <div className="sr-type-head">
        <span className="sr-type-name">{label}</span>
        {has ? (
          <span className="sr-type-pill sr-type-own">Its own stages</span>
        ) : (
          <span className="sr-type-pill sr-type-inherit">
            <span className="sr-type-pill-ic" aria-hidden="true">
              ↳
            </span>{' '}
            Uses the board default
          </span>
        )}
        {has && !open && stages.length > 0 && (
          <span className="sr-type-chain">{chainSummary(stages)}</span>
        )}

        {canSettings && (
          <span className="sr-type-actions">
            {!has ? (
              <button className="link" onClick={onOverride}>
                Override
              </button>
            ) : open ? (
              <button className="link" onClick={onReset}>
                Reset to default
              </button>
            ) : (
              <>
                <button className="link" onClick={onToggleOpen}>
                  Edit
                </button>
                <span className="sr-type-dot" aria-hidden="true">
                  ·
                </span>
                <button className="link" onClick={onReset}>
                  Reset
                </button>
              </>
            )}
          </span>
        )}
      </div>

      {has && open && (
        <div className="sr-type-editor">
          <p className="sr-type-editor-head grp-heading">{label} signs off as</p>
          <StageEditor
            stages={stages}
            onChange={onChange}
            world={world}
            profiles={profiles}
            canSettings={canSettings}
            emptyNote={`Add a stage and say who signs off a ${label.toLowerCase()}.`}
          />
        </div>
      )}
    </div>
  );
}

